import React, { useEffect, useState, useCallback } from 'react';
import { Phone, DollarSign, AlertTriangle, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useDataChangeListener } from '../utils/dataBus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ObjectifsCaMensuels = ({ dashboardPeriod, dashboardSelectedMonth }) => {
  const { user } = useAuth();
  const [caMensuel, setCaMensuel] = useState(null);
  const [animatedValues, setAnimatedValues] = useState({});
  
  // Si le dashboard a sélectionné un mois (mode customMonth), utiliser celui-là
  // Sinon, utiliser le mois en cours par défaut
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  
  // Déterminer le mois et l'année à afficher
  const effectiveMonth = dashboardPeriod === 'customMonth' && dashboardSelectedMonth 
    ? dashboardSelectedMonth.month - 1 // Convertir de 1-indexed à 0-indexed
    : selectedMonth;
  const effectiveYear = dashboardPeriod === 'customMonth' && dashboardSelectedMonth
    ? dashboardSelectedMonth.year
    : selectedYear;

  // Fonction pour obtenir la date formatée
  const getFormattedDate = () => {
    const date = new Date(selectedYear, selectedMonth, new Date().getDate());
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString('fr-FR', options);
  };

  // Fonction pour obtenir le nom du mois
  const getMonthName = (monthIndex) => {
    const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    return months[monthIndex];
  };

  // Mois actuel et mois précédent
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;

  // Fetch CA data based on effective month (dashboard month or local selection)
  const fetchCaMensuel = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/dashboard/objectifs-ca-mensuels`, {
        params: {
          month: effectiveMonth + 1,
          year: effectiveYear
        },
        withCredentials: true
      });
      setCaMensuel(response.data || null);
    } catch (error) {
      console.error('Erreur objectifs CA:', error);
      setCaMensuel(null);
    } finally {
      setLoading(false);
    }
  }, [effectiveMonth, effectiveYear]);

  useEffect(() => {
    fetchCaMensuel();
  }, [fetchCaMensuel, dashboardPeriod, dashboardSelectedMonth]);

  // Refresh instantané quand une transaction/dépense/dépôt change (via dataBus)
  useDataChangeListener(fetchCaMensuel);

  // Animation des chiffres
  useEffect(() => {
    if (caMensuel?.commerciaux) {
      const animated = {};
      caMensuel.commerciaux.forEach(commercial => {
        animated[commercial.id] = 0;
      });
      setAnimatedValues(animated);

      const interval = setInterval(() => {
        setAnimatedValues(prev => {
          const newValues = { ...prev };
          let allDone = true;
          caMensuel.commerciaux.forEach(commercial => {
            if (newValues[commercial.id] < commercial.caRealise) {
              newValues[commercial.id] = Math.min(
                newValues[commercial.id] + Math.ceil(commercial.caRealise / 30),
                commercial.caRealise
              );
              allDone = false;
            }
          });
          if (allDone) clearInterval(interval);
          return newValues;
        });
      }, 30);

      return () => clearInterval(interval);
    }
  }, [caMensuel]);

  if (loading) {
    return (
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1C2541]/90 to-[#2A3B5C]/90 backdrop-blur-xl p-8 border-2 border-[#C6A15B]/30">
        <div className="flex justify-center items-center py-20">
          <div className="text-[#C6A15B] text-lg">Chargement...</div>
        </div>
      </div>
    );
  }

  // CEO Session 5 — Message explicite si le user n'est pas lié à une fiche commerciale
  if (caMensuel && caMensuel.no_commercial_mapped) {
    return (
      <div
        data-testid="no-commercial-mapped-banner"
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1C2541]/90 to-[#2A3B5C]/90 backdrop-blur-xl p-8 border-2 border-[#F59E0B]/40 mb-6"
      >
        <div className="text-center py-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#F59E0B]/20 border border-[#F59E0B]/40 mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h3 className="text-[#F59E0B] text-xl font-bold mb-2">Objectifs CA Mensuels</h3>
          <p className="text-[#EAEAEA]/80 text-base">
            Aucun commercial lié à votre compte. Contactez l'administrateur.
          </p>
        </div>
      </div>
    );
  }

  if (!caMensuel || !caMensuel.commerciaux || caMensuel.commerciaux.length === 0) {
    return null;
  }

  // CEO Session 5 — Le filtrage est désormais fait côté backend selon le rôle.
  // Le frontend se contente d'afficher ce que le backend lui renvoie, trié par CA.
  const commerciaux = [...caMensuel.commerciaux].sort((a, b) => b.caRealise - a.caRealise);

  // Si la liste est vide après filtrage backend, rien à afficher
  if (commerciaux.length === 0) {
    return null;
  }

  const getBadge3D = (index) => {
    const badges = [
      { 
        icon: '🥇', 
        label: '1er', 
        gradient: 'from-yellow-400 via-amber-300 to-yellow-600',
        shadow: 'shadow-[0_8px_30px_rgb(251,191,36,0.4)]',
        glow: 'drop-shadow-[0_0_15px_rgba(251,191,36,0.7)]'
      },
      { 
        icon: '🥈', 
        label: '2ème',
        gradient: 'from-gray-300 via-slate-200 to-gray-400',
        shadow: 'shadow-[0_8px_30px_rgb(148,163,184,0.4)]',
        glow: 'drop-shadow-[0_0_15px_rgba(148,163,184,0.7)]'
      },
      { 
        icon: '🥉', 
        label: '3ème',
        gradient: 'from-orange-400 via-amber-500 to-orange-600',
        shadow: 'shadow-[0_8px_30px_rgb(251,146,60,0.4)]',
        glow: 'drop-shadow-[0_0_15px_rgba(251,146,60,0.7)]'
      }
    ];
    return badges[index] || badges[2];
  };

  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1C2541]/90 to-[#2A3B5C]/90 backdrop-blur-xl p-8 border-2 border-[#C6A15B]/30">
      {/* Effet de lumière ambiante */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(198,161,91,0.08),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(59,130,246,0.06),transparent_50%)]"></div>

      <div className="relative z-10">
        {/* Header Premium avec date et sélecteur de mois */}
        <div className="mb-10 flex items-center justify-between">
          {/* Titre avec date */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-3xl filter drop-shadow-[0_0_20px_rgba(251,191,36,0.6)]">
              <span>🏆</span>
              <span>⭐</span>
            </div>
            <div>
              <h2 className="text-3xl font-black bg-gradient-to-r from-[#FFD700] via-[#C6A15B] to-[#FFD700] bg-clip-text text-transparent"
                style={{ textShadow: '0 0 30px rgba(198,161,91,0.5)' }}
              >
                Objectifs CA Mensuels
              </h2>
              <p className="text-sm text-[#C6A15B]/70 mt-1 font-light">
                {getFormattedDate()}
              </p>
            </div>
          </div>

          {/* Sélecteur de mois - Design Premium et Discret */}
          {/* Désactiver si le Dashboard a sélectionné un mois */}
          {dashboardPeriod !== 'customMonth' && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setSelectedMonth(previousMonth);
                  setSelectedYear(previousYear);
                }}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 
                  ${selectedMonth === previousMonth && selectedYear === previousYear
                    ? 'bg-gradient-to-r from-[#FFD700]/20 to-[#C6A15B]/20 border-2 border-[#FFD700]/40 text-[#FFD700] shadow-[0_0_20px_rgba(255,215,0,0.3)]'
                    : 'bg-[#1C2541]/60 border border-[#C6A15B]/20 text-[#C6A15B]/70 hover:border-[#C6A15B]/40 hover:text-[#C6A15B]'
                  }`}
              >
                {getMonthName(previousMonth)}
              </button>
              <button
                onClick={() => {
                  setSelectedMonth(currentMonth);
                  setSelectedYear(currentYear);
                }}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 
                  ${selectedMonth === currentMonth && selectedYear === currentYear
                    ? 'bg-gradient-to-r from-[#FFD700]/20 to-[#C6A15B]/20 border-2 border-[#FFD700]/40 text-[#FFD700] shadow-[0_0_20px_rgba(255,215,0,0.3)]'
                    : 'bg-[#1C2541]/60 border border-[#C6A15B]/20 text-[#C6A15B]/70 hover:border-[#C6A15B]/40 hover:text-[#C6A15B]'
                  }`}
              >
                {getMonthName(currentMonth)}
              </button>
            </div>
          )}
          
          {/* Si Dashboard a sélectionné un mois, afficher uniquement ce mois */}
          {dashboardPeriod === 'customMonth' && dashboardSelectedMonth && (
            <div className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-[#FFD700]/20 to-[#C6A15B]/20 border-2 border-[#FFD700]/40 text-[#FFD700] shadow-[0_0_20px_rgba(255,215,0,0.3)]">
              {getMonthName(effectiveMonth)} {effectiveYear}
            </div>
          )}
        </div>

        {/* Grille des commerciaux - 3 colonnes alignées */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {commerciaux.map((commercial, index) => {
            const pourcentage = commercial.pourcentage || 0;
            const animatedCA = animatedValues[commercial.id] || 0;
            const resteARealiser = commercial.resteARealiser || 0;
            const enRetard = pourcentage < 70;
            const badge = getBadge3D(index);
            
            // Couleurs vives inspirées de la Section 1
            const colorSchemes = [
              { main: '#22D3EE', border: '#22D3EE', bg: 'from-[#22D3EE]/10 to-transparent', shadow: '[#22D3EE]/30' }, // Cyan/Turquoise (Emmanuel)
              { main: '#F472B6', border: '#F472B6', bg: 'from-[#F472B6]/10 to-transparent', shadow: '[#F472B6]/30' }, // Rose (Gisèle)
              { main: '#FB923C', border: '#FB923C', bg: 'from-[#FB923C]/10 to-transparent', shadow: '[#FB923C]/30' }  // Orange (Ange)
            ];
            const colorScheme = colorSchemes[index % 3];

            return (
              <div 
                key={commercial.id} 
                className={`group relative bg-gradient-to-br ${colorScheme.bg} backdrop-blur-xl rounded-2xl p-6 border-2 border-${colorScheme.border}/40
                  hover:border-${colorScheme.border}/60 hover:shadow-[0_20px_60px_-15px_rgba(255,215,0,0.3)] 
                  transition-all duration-700 ease-out hover:scale-[1.02] hover:-translate-y-1`}
                style={{
                  borderColor: `${colorScheme.border}66`,
                  boxShadow: `0 10px 40px -10px ${colorScheme.shadow}`
                }}
              >
                {/* Badge 3D métallique en haut à droite */}
                <div className={`absolute -top-4 -right-4 w-20 h-20 rounded-full bg-gradient-to-br ${badge.gradient} ${badge.shadow} 
                  flex items-center justify-center transform hover:scale-125 hover:rotate-12 transition-transform duration-500
                  border-4 border-[#1C2541] ${badge.glow} z-20`}
                  style={{ 
                    boxShadow: 'inset 0 3px 10px rgba(255,255,255,0.4), inset 0 -3px 10px rgba(0,0,0,0.4), 0 15px 50px rgba(0,0,0,0.5)'
                  }}
                >
                  <div className="text-center">
                    <div className="text-3xl mb-0.5 filter drop-shadow-lg">{badge.icon}</div>
                    <div className="text-sm font-black text-[#0a0f1e] tracking-wide">{badge.label}</div>
                  </div>
                </div>

                {/* Nom et Poste */}
                <div className="mb-6">
                  <h3 className="text-2xl font-black mb-2"
                    style={{ 
                      color: colorScheme.main,
                      textShadow: `0 0 20px ${colorScheme.main}80`
                    }}
                  >
                    {commercial.nom}
                  </h3>
                  {commercial.poste && (
                    <p className="text-[#C6A15B] text-sm font-light tracking-wide uppercase opacity-90">
                      {commercial.poste}
                    </p>
                  )}
                </div>

                {/* Barre de progression Premium avec effet lumineux */}
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-slate-400/80 font-light">Progression</span>
                    <span className="text-2xl font-black"
                      style={{ 
                        color: colorScheme.main,
                        textShadow: `0 0 15px ${colorScheme.main}60`
                      }}
                    >
                      {Math.round(pourcentage)}%
                    </span>
                  </div>
                  <div className="relative h-4 bg-slate-900/50 rounded-full overflow-hidden border border-slate-700/30">
                    {/* Effet de reflet sur le fond */}
                    <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent"></div>
                    
                    {/* Barre de progression avec dégradé lumineux */}
                    <div 
                      className="absolute inset-y-0 left-0 rounded-full transition-all duration-1500 ease-out"
                      style={{ 
                        width: `${Math.min(pourcentage, 100)}%`,
                        background: `linear-gradient(90deg, ${colorScheme.main}CC, ${colorScheme.main})`,
                        boxShadow: `0 0 20px ${colorScheme.main}60, inset 0 1px 3px rgba(255,255,255,0.3)`
                      }}
                    >
                      {/* Effet de brillance animée */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent 
                        animate-shimmer bg-[length:200%_100%]"></div>
                    </div>
                  </div>
                </div>

                {/* CA Actuel et Objectif - Typographie ajustée selon hiérarchie */}
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1">
                    <div className="text-sm text-slate-400/70 font-light mb-2 uppercase tracking-wider">CA Actuel</div>
                    <div className="text-3xl font-black mb-1"
                      style={{ 
                        color: colorScheme.main,
                        textShadow: `0 0 30px ${colorScheme.main}50, 0 0 60px ${colorScheme.main}30`
                      }}
                    >
                      {animatedCA.toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-500 font-light">FCFA</div>
                  </div>
                  <div className="text-right flex-1">
                    <div className="text-sm text-slate-400/70 font-light mb-2 uppercase tracking-wider">Objectif</div>
                    <div className="text-2xl font-bold text-[#C6A15B] mb-1"
                      style={{ textShadow: '0 0 20px rgba(198,161,91,0.4)' }}
                    >
                      {commercial.objectifCA.toLocaleString()}
                    </div>
                    <div className="text-sm text-slate-500 font-light">FCFA/mois</div>
                  </div>
                </div>

                {/* Reste à réaliser - Effet néon doré */}
                <div className="bg-gradient-to-r from-[#C6A15B]/5 to-transparent rounded-2xl p-5 mb-5 
                  border border-[#C6A15B]/20 backdrop-blur-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-400/80 font-light">Reste à réaliser</span>
                    <div className="text-right">
                      <div className="text-3xl font-black text-[#FFD700]"
                        style={{ textShadow: '0 0 25px rgba(255,215,0,0.6)' }}
                      >
                        {resteARealiser.toLocaleString()}
                      </div>
                      <div className="text-sm text-[#C6A15B]/70 font-light">FCFA</div>
                    </div>
                  </div>
                </div>

                {/* Salaires - Design élégant avec détails de commission */}
                <div className="space-y-3 mb-6 py-4 border-t border-slate-700/30 bg-gradient-to-br from-slate-900/20 to-transparent rounded-xl px-3">
                  {/* En-tête Performance */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400/60 font-light uppercase tracking-wider">Performance Salaire</span>
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                      commercial.tauxRealisation >= 100 ? 'bg-green-500/20 text-green-400' :
                      commercial.tauxRealisation >= 75 ? 'bg-yellow-500/20 text-yellow-400' :
                      commercial.tauxRealisation >= 50 ? 'bg-orange-500/20 text-orange-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {commercial.performance || 'N/A'}
                    </span>
                  </div>

                  {/* Salaire Fixe */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400/70">💰</span>
                      <span className="text-sm text-slate-400/80 font-light">Salaire Fixe</span>
                    </div>
                    <div className="text-base font-semibold text-slate-300/90">
                      {commercial.salaireFix?.toLocaleString() || 0} F
                    </div>
                  </div>
                  
                  {/* Commission Variable — peut être négative (CEO formule cumul journalier) */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${commercial.commissionVariable >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {commercial.commissionVariable >= 0 ? '📈' : '📉'}
                      </span>
                      <span className={`text-sm font-light ${commercial.commissionVariable >= 0 ? 'text-green-400/80' : 'text-red-400/80'}`}>
                        Commission cumulée
                      </span>
                    </div>
                    <div className={`text-base font-semibold ${commercial.commissionVariable >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {commercial.commissionVariable >= 0 ? '+' : ''}{(commercial.commissionVariable || 0).toLocaleString()} F
                    </div>
                  </div>

                  {/* Détail : objectif cumulé × jours ouvrés écoulés */}
                  <div className="text-xs text-slate-500/60 italic text-right">
                    {commercial.objectifJournalier > 0
                      ? `Objectif cumulé : ${(commercial.objectifCumule || 0).toLocaleString()} F (${(commercial.objectifJournalier || 0).toLocaleString()} F/j × ${commercial.nbJoursOuvresEcoules || 0} j)`
                      : 'Objectif journalier non défini'}
                  </div>

                  {/* Séparateur */}
                  <div className="border-t border-[#C6A15B]/20 my-2"></div>
                  
                  {/* Salaire Net FINAL */}
                  <div className="flex items-center justify-between bg-gradient-to-r from-[#C6A15B]/10 to-transparent rounded-lg p-3 border border-[#C6A15B]/30">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-[#FFD700]" />
                      <span className="text-sm text-[#FFD700] font-bold uppercase tracking-wide">Salaire Net</span>
                    </div>
                    <div className="text-2xl font-black text-[#FFD700]"
                      style={{ textShadow: '0 0 20px rgba(255,215,0,0.5)' }}
                    >
                      {commercial.salaireNet?.toLocaleString() || 0} F
                    </div>
                  </div>

                  {/* Taux de réalisation */}
                  <div className="text-center text-xs text-slate-400/60 mt-2">
                    Réalisation: {commercial.tauxRealisation?.toFixed(1) || 0}%
                  </div>

                  {/* CEO Session 12 — Projection fin de mois "Si tu maintiens ce rythme" */}
                  {commercial.projectionDisponible && commercial.salaireProjeteFinMois !== null && (
                    <div className="mt-3 rounded-lg border border-[#3B82F6]/30 bg-gradient-to-br from-[#3B82F6]/10 to-transparent p-3">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className="text-[10px] uppercase tracking-wider text-[#93C5FD] font-bold">
                          🎯 Projection fin de mois
                        </span>
                      </div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-400/80">Si tu maintiens ce rythme :</span>
                        <span className={`text-base font-black ${
                          commercial.salaireProjeteFinMois > commercial.salaireFix
                            ? 'text-green-400'
                            : commercial.salaireProjeteFinMois < commercial.salaireFix
                              ? 'text-red-400'
                              : 'text-[#FFD700]'
                        }`}>
                          {(commercial.salaireProjeteFinMois || 0).toLocaleString()} F
                        </span>
                      </div>
                      <div className="text-[10px] text-slate-500/70 italic">
                        {commercial.nbJoursOuvresRestants > 0
                          ? `${commercial.nbJoursOuvresRestants} j ouvré${commercial.nbJoursOuvresRestants > 1 ? 's' : ''} restant${commercial.nbJoursOuvresRestants > 1 ? 's' : ''} • CA projeté : ${(commercial.caProjeteFinMois || 0).toLocaleString()} F`
                          : 'Dernier jour ouvré du mois'}
                      </div>
                    </div>
                  )}
                </div>

                {/* Alerte Premium avec fond translucide et bordure lumineuse */}
                {enRetard && (
                  <div className="relative bg-red-500/5 backdrop-blur-md rounded-xl p-3 
                    border border-red-400/30 shadow-[0_0_20px_rgba(239,68,68,0.1)]
                    hover:border-red-400/50 transition-all duration-500">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <AlertTriangle className="w-4 h-4 text-red-400 animate-pulse" />
                        <div className="absolute inset-0 w-4 h-4 text-red-400 animate-ping opacity-30"></div>
                      </div>
                      <span className="text-xs font-semibold text-red-300/90">
                        ⚠️ En retard sur ton objectif du mois
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Section Performance Équipe - Design Premium */}
        <div className="relative bg-gradient-to-br from-[#1a1f35]/60 via-[#0f1526]/70 to-[#0a0f1e]/80 
          rounded-3xl p-8 border border-[#C6A15B]/15 backdrop-blur-xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-4 h-4 bg-gradient-to-br from-[#10B981] to-[#059669] rounded-full 
              shadow-[0_0_20px_rgba(16,185,129,0.6)] animate-pulse"></div>
            <h3 className="text-2xl font-bold bg-gradient-to-r from-[#10B981] to-[#34D399] bg-clip-text text-transparent">
              Performance Équipe
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            {/* Objectif Total */}
            <div className="bg-gradient-to-br from-[#10B981]/10 to-transparent rounded-2xl p-6 
              border border-[#10B981]/20 backdrop-blur-sm">
              <div className="text-sm text-slate-400/70 font-light mb-3 uppercase tracking-wider">Objectif Total</div>
              <div className="text-4xl font-black mb-2"
                style={{ 
                  color: '#10B981',
                  textShadow: '0 0 30px rgba(16,185,129,0.5)'
                }}
              >
                {caMensuel.objectifTotal.toLocaleString()}
              </div>
              <div className="text-sm text-slate-500 font-light">FCFA/mois</div>
            </div>

            {/* CA Actuel */}
            <div className="bg-gradient-to-br from-[#FFD700]/10 to-transparent rounded-2xl p-6 
              border border-[#FFD700]/20 backdrop-blur-sm">
              <div className="text-sm text-slate-400/70 font-light mb-3 uppercase tracking-wider">CA Actuel (Ce Mois)</div>
              <div className="text-4xl font-black mb-2"
                style={{ 
                  color: '#FFD700',
                  textShadow: '0 0 30px rgba(255,215,0,0.5)'
                }}
              >
                {caMensuel.caTotal.toLocaleString()}
              </div>
              <div className="text-sm text-slate-500 font-light">FCFA</div>
            </div>

            {/* Salaire Total Actuel - NOUVEAU */}
            <div className="bg-gradient-to-br from-[#C6A15B]/10 to-transparent rounded-2xl p-6 
              border border-[#C6A15B]/20 backdrop-blur-sm">
              <div className="text-sm text-slate-400/70 font-light mb-3 uppercase tracking-wider">Salaire Total Actuel</div>
              <div className="text-4xl font-black mb-2"
                style={{ 
                  color: '#C6A15B',
                  textShadow: '0 0 30px rgba(198,161,91,0.5)'
                }}
              >
                {caMensuel.salaireTotalActuel?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-slate-500 font-light">FCFA</div>
            </div>

            {/* Progression */}
            <div className="bg-gradient-to-br from-[#3B82F6]/10 to-transparent rounded-2xl p-6 
              border border-[#3B82F6]/20 backdrop-blur-sm">
              <div className="text-sm text-slate-400/70 font-light mb-3 uppercase tracking-wider">Progression Mensuelle</div>
              <div className="text-4xl font-black mb-2"
                style={{ 
                  color: '#3B82F6',
                  textShadow: '0 0 30px rgba(59,130,246,0.5)'
                }}
              >
                {Math.round((caMensuel.caTotal / caMensuel.objectifTotal) * 100)}%
              </div>
              <div className="text-sm text-slate-500 font-light">de l'objectif</div>
            </div>
          </div>
        </div>
      </div>

      {/* Styles d'animation personnalisés */}
      <style jsx>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
      `}</style>
    </div>
  );
};

export default ObjectifsCaMensuels;
